import React from "react";
import { useParams } from "react-router-dom";

import moment from "moment-timezone";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import startCase from "lodash/startCase";
import { formatTimestampLong } from "utils/format";
import { faExclamationTriangle } from "@fortawesome/pro-solid-svg-icons";

import { useRoutes } from "utils/routes";

import PanelSection from "ui/PanelSection";
import Avatar from "ui/Avatar";
import PageContent from "ui/PageContent";
import Panel from "ui/Panel";
import SettingsNav from "ui/SettingsNav";
import Loading from "ui/Loading";

import styles from "./style.module.scss";
import ResetIntegration from "./ResetIntegration";
import ConnectTestSlack from "./ConnectTestSlack";
import ConnectTestPagerDuty from "./ConnectTestPagerDuty";
import SetupSamlIntegration from "./SetupSamlIntegration";
import ContactSupportLink from "ui/ContactSupportLink";
import { useFeature } from "providers/OrganizationFeatures";
import Callout from "ui/Callout";
import UpdateRoleSaml from "./UpdateRoleSaml";
import WebhooksPanel from "./Webhooks";
import IncidentIoPanel from "./IncidentIo";
import UpdateCertSaml from "./UpdateCertSaml";
import {
  useOrganizationIntegrationsQuery,
  OrganizationIntegrationsQuery,
} from "./gql/Query.generated";

type SlackIntegrationType =
  OrganizationIntegrationsQuery["getOrganizationDetails"]["slackIntegration"];
type PagerdutyIntegrationType =
  OrganizationIntegrationsQuery["getOrganizationDetails"]["pagerdutyIntegration"];
type SamlIntegrationType =
  OrganizationIntegrationsQuery["getOrganizationDetails"]["samlIntegration"];
type OrganizationRoleType =
  OrganizationIntegrationsQuery["getOrganizationRoles"][number];
type IntegrationType = SlackIntegrationType & PagerdutyIntegrationType;
type IntegrationStatusType = "enabled" | "not enabled" | "auth invalid";

const OrganizationIntegrations: React.FunctionComponent = () => {
  const { slug: organizationSlug } = useParams();

  const { data, loading, error } = useOrganizationIntegrationsQuery({
    variables: {
      organizationSlug,
    },
  });

  if (loading || error) {
    return <Loading error={!!error} />;
  }

  const { permittedToEditOrganization } = data.getOrganizationDetails;

  return (
    <PageContent
      title="Integrations"
      pageCategory="organization"
      pageName="integrations"
      featureNav={<SettingsNav />}
    >
      <div className="max-w-[1200px]">
        <SlackPanel
          organizationSlug={organizationSlug}
          integration={data.getOrganizationDetails.slackIntegration}
          canEdit={permittedToEditOrganization}
        />
        <PagerDutyPanel
          organizationSlug={organizationSlug}
          integration={data.getOrganizationDetails.pagerdutyIntegration}
          canEdit={permittedToEditOrganization}
        />
        <WebhooksPanel
          organizationSlug={organizationSlug}
          endpoints={data.getOrganizationDetails.webhookEndpoints}
          signingKeys={data.getWebhookSigningKeys}
          canEdit={permittedToEditOrganization}
        />
        <IncidentIoPanel
          organizationSlug={organizationSlug}
          endpoints={data.getOrganizationDetails.incidentIoEndpoints}
          canEdit={permittedToEditOrganization}
        />
        <SamlPanel
          organizationSlug={organizationSlug}
          integration={data.getOrganizationDetails.samlIntegration}
          isTrial={data.getOrganizationDetails.planInfo.isTrial}
          defaultSamlRole={data.getOrganizationDetails.defaultSamlRole}
          roles={data.getOrganizationRoles}
          canEdit={permittedToEditOrganization}
        />
      </div>
    </PageContent>
  );
};

const SlackPanel: React.FunctionComponent<{
  organizationSlug: string;
  integration: SlackIntegrationType;
  canEdit: boolean;
}> = ({ organizationSlug, integration, canEdit }) => {
  const status = getIntegrationStatus(integration);
  return (
    <Panel
      title="Slack"
      secondaryTitle={<IntegrationInfo info={integration} />}
    >
      <PanelSection>
        <IntegrationStatus
          organizationSlug={organizationSlug}
          status={status}
          canEdit={canEdit}
        />
        {canEdit ? (
          <div className={styles.integrationGrid}>
            <ConnectTestSlack
              organizationSlug={organizationSlug}
              hasChannelError={!!integration?.availableChannels.error}
              channels={integration?.availableChannels.channels}
              status={status}
            />
            <ResetIntegration
              integrationId={integration?.id}
              service="Slack"
              disabled={status === "not enabled"}
              alertingIntegration
            />
          </div>
        ) : (
          <div className={styles.noEdit}>
            You are not allowed to edit integration settings for this
            organization.
          </div>
        )}
      </PanelSection>
    </Panel>
  );
};

const PagerDutyPanel: React.FunctionComponent<{
  organizationSlug: string;
  integration: PagerdutyIntegrationType;
  canEdit: boolean;
}> = ({ organizationSlug, integration, canEdit }) => {
  const status = getIntegrationStatus(integration);
  return (
    <Panel
      title="PagerDuty"
      secondaryTitle={<IntegrationInfo info={integration} />}
    >
      <PanelSection>
        <IntegrationStatus
          organizationSlug={organizationSlug}
          status={status}
          canEdit={canEdit}
        />
        {canEdit ? (
          <div className={styles.integrationGrid}>
            <ConnectTestPagerDuty
              organizationSlug={organizationSlug}
              services={integration?.availableServices}
              status={status}
            />
            <ResetIntegration
              integrationId={integration?.id}
              service="PagerDuty"
              disabled={status === "not enabled"}
              alertingIntegration
            />
          </div>
        ) : (
          <div className={styles.noEdit}>
            You are not allowed to edit integration settings for this
            organization.
          </div>
        )}
      </PanelSection>
    </Panel>
  );
};

const SamlPanel: React.FunctionComponent<{
  organizationSlug: string;
  integration: SamlIntegrationType;
  isTrial: boolean;
  defaultSamlRole: OrganizationRoleType;
  roles: OrganizationRoleType[];
  canEdit: boolean;
}> = ({
  organizationSlug,
  integration,
  isTrial,
  defaultSamlRole,
  roles,
  canEdit,
}) => {
  const hasSaml = useFeature("saml");
  const status = getIntegrationStatus(integration);
  return (
    <Panel
      title="Single Sign-On with SAML (Okta, etc)"
      secondaryTitle={<IntegrationInfo info={integration} />}
    >
      {!hasSaml ? (
        <PanelSection>
          This feature is not available{" "}
          {isTrial ? "during your pganalyze trial" : "with your pganalyze plan"}
          . If you have questions about Single Sign-On functionality, please{" "}
          <ContactSupportLink>contact support</ContactSupportLink>.
        </PanelSection>
      ) : (
        <>
          <PanelSection>
            <IntegrationStatus
              organizationSlug={organizationSlug}
              status={status}
              canEdit={false}
            />
          </PanelSection>
          <PanelSection>
            {integration ? (
              <div>
                <strong>IdP Entity ID</strong>
                {": "}
                <code>{integration.idpEntityId}</code>
              </div>
            ) : null}
            {canEdit ? (
              <>
                <div className={styles.integrationGrid}>
                  <UpdateRoleSaml
                    organizationSlug={organizationSlug}
                    defaultRole={defaultSamlRole}
                    roles={roles}
                    disabled={!integration || status === "not enabled"}
                  />
                  {status == "enabled" && (
                    <UpdateCertSaml
                      organizationSlug={organizationSlug}
                      idpCertExpiresAt={integration?.idpCertExpiresAt}
                    />
                  )}
                  <ResetIntegration
                    integrationId={integration?.id}
                    service="SAML"
                    disabled={!integration || status === "not enabled"}
                    alertingIntegration={false}
                    hideIfDisabled
                  />
                </div>
                <SetupSamlIntegration
                  organizationSlug={organizationSlug}
                  integration={integration}
                  defaultRole={defaultSamlRole}
                  roles={roles}
                />
              </>
            ) : (
              <div className={styles.noEdit}>
                You are not allowed to edit integration settings for this
                organization.
              </div>
            )}
          </PanelSection>
          <Callout
            className="m-[10px]"
            learnMoreLink="https://pganalyze.com/docs/accounts/sso"
          >
            <ul className="pl-4">
              <li>
                New members will receive the{" "}
                <strong>
                  "{defaultSamlRole?.name ?? "View & Modify (All Servers)"}"
                </strong>{" "}
                role in this organization
              </li>
              <li>
                Any existing username/password users in your organization who
                subsequently authenticate through SSO will be converted to SSO
                accounts tied to your organization
              </li>
              <li>
                User deprovisioning is not yet supported - once access is
                revoked users can't sign in anymore but still show up in
                pganalyze
              </li>
            </ul>
          </Callout>
        </>
      )}
    </Panel>
  );
};

type IntegrationInfo = Pick<IntegrationType, "createdAt" | "integratedBy">;

const IntegrationInfo: React.FunctionComponent<{
  info: IntegrationInfo;
}> = ({ info }) => {
  if (!info) {
    return null;
  }

  const createdAt = formatTimestampLong(moment.unix(info.createdAt));
  const addedBy = info.integratedBy.fullname;
  const addedByAvatarUrl = info.integratedBy.avatarUrl;
  return (
    <span className="text-[#595959]">
      Added {createdAt} by{" "}
      <Avatar name={addedBy} avatarUrl={addedByAvatarUrl} />
    </span>
  );
};

const IntegrationStatus: React.FunctionComponent<{
  organizationSlug: string;
  status: IntegrationStatusType;
  canEdit: boolean;
}> = ({ organizationSlug, status, canEdit }) => {
  const { organizationAlerts } = useRoutes();
  let statusDescription;
  if (status === "enabled") {
    statusDescription = (
      <>
        The integration is enabled.
        {canEdit && (
          <>
            {" "}
            Go to{" "}
            <Link to={organizationAlerts(organizationSlug)}>
              Alerts &amp; Check-Up configuration
            </Link>{" "}
            and select a server to configure it.
          </>
        )}
      </>
    );
  } else if (status === "not enabled") {
    statusDescription = (
      <>
        The integration is not enabled.
        {canEdit && <> Click below to enable it.</>}
      </>
    );
  } else if (status === "auth invalid") {
    statusDescription = (
      <>
        There was a problem with the integration credentials.
        {canEdit && (
          <> Try resetting the integration and configuring it again.</>
        )}
      </>
    );
  }
  const statusLabel = startCase(status);
  return (
    <span>
      {status === "auth invalid" && (
        <FontAwesomeIcon
          className="mr-1 text-[#c93a2a]"
          icon={faExclamationTriangle}
        />
      )}
      <strong>{statusLabel}</strong>
      {": "}
      {statusDescription}
    </span>
  );
};

function getIntegrationStatus(
  integration: Pick<IntegrationType, "authInvalid">,
): IntegrationStatusType {
  if (integration?.authInvalid) {
    return "auth invalid";
  } else if (integration) {
    return "enabled";
  } else {
    return "not enabled";
  }
}

export default OrganizationIntegrations;
